import React, { useEffect, useState } from 'react';
import axios from 'axios';
import moment from 'moment';
import { v4 as uuidv4 } from 'uuid';
import './EventDetailsModal.css';

const EventDetailsModal = ({ show, event, formData, handleChange, onSave, onClose, onDelete, clients: passedClients }) => {
  const [clientsList, setClients] = useState(passedClients || []);
  const [addVideoConference, setAddVideoConference] = useState(formData?.addVideoConference || false);
  const [hostLink, setHostLink] = useState(formData?.hostLink || '');
  const [guestLink, setGuestLink] = useState(formData?.guestLink || '');
  const [linkCopied, setLinkCopied] = useState(false);

  useEffect(() => {
    if (show) {
      if (passedClients && passedClients.length > 0) {
        setClients(passedClients);
      } else {
        const fetchClients = async () => {
          const token = localStorage.getItem('token');
          try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/clients`, {
              headers: { Authorization: `Bearer ${token}` },
            });
            setClients(response.data);
          } catch (error) {
            console.error('Failed to load clients:', error);
          }
        };
        fetchClients();
      }

      setAddVideoConference(formData?.addVideoConference || !!(event?.hostLink && event?.guestLink));
      setHostLink(formData?.hostLink || event?.hostLink || '');
      setGuestLink(formData?.guestLink || event?.guestLink || '');
    }
  }, [show, event, passedClients]);

  const handleAddVideoConferenceToggle = () => {
    const newState = !addVideoConference;
    setAddVideoConference(newState);

    if (newState) {
      const roomId = uuidv4();
      const newHostLink = `${window.location.origin}/host-conference?room=${roomId}`;
      const newGuestLink = `${window.location.origin}/guest-conference?room=${roomId}`;
      setHostLink(newHostLink);
      setGuestLink(newGuestLink);
      handleChange({ target: { name: 'addVideoConference', value: true } });
      handleChange({ target: { name: 'hostLink', value: newHostLink } });
      handleChange({ target: { name: 'guestLink', value: newGuestLink } });
    } else {
      setHostLink('');
      setGuestLink('');
      handleChange({ target: { name: 'addVideoConference', value: false } });
      handleChange({ target: { name: 'hostLink', value: '' } });
      handleChange({ target: { name: 'guestLink', value: '' } });
    }
  };

  const handleClientChange = (e) => {
    const selectedClient = clientsList.find((client) => client._id === e.target.value);
    if (selectedClient) {
      handleChange({ target: { name: 'clientId', value: selectedClient._id } });
      handleChange({ target: { name: 'clientName', value: `${selectedClient.firstName} ${selectedClient.lastName}` } });
      handleChange({ target: { name: 'clientEmail', value: selectedClient.email } });
    } else {
      handleChange({ target: { name: 'clientId', value: '' } });
      handleChange({ target: { name: 'clientName', value: '' } });
      handleChange({ target: { name: 'clientEmail', value: '' } });
    }
  };

  const handleCopyGuestLink = () => {
    navigator.clipboard.writeText(guestLink);
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.date || !formData.startTime || !formData.endTime) {
      alert('Please fill in all required fields.');
      return;
    }

    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
    const startDateTime = moment.tz(`${formData.date}T${formData.startTime}`, userTimezone).utc().toISOString();
    const endDateTime = moment.tz(`${formData.date}T${formData.endTime}`, userTimezone).utc().toISOString();

    let updatedFormData = { ...formData, startTime: startDateTime, endTime: endDateTime };

    // Video conference disabled — clear links
    updatedFormData.hostLink = '';
    updatedFormData.guestLink = '';
    updatedFormData.addVideoConference = false;

    await onSave(updatedFormData);
    onClose();
  };

  if (!show) return null;

  return (
    <div className="edm-overlay" onClick={onClose}>
      <div className="edm-modal" onClick={(e) => e.stopPropagation()}>
        {/* Close button */}
        <button className="edm-close-x" onClick={onClose} aria-label="Close">✕</button>

        {/* Header */}
        <div className="edm-header">
          <div className="edm-header-icon">{event ? '✏️' : '📅'}</div>
          <h2>{event ? 'Edit Event' : 'Create New Event'}</h2>
          <p className="edm-subtitle">
            {event ? 'Update the details for this event' : 'Fill in the details for your new event'}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="edm-form">
          {/* Section: Event Details */}
          <div className="edm-section">
            <div className="edm-section-label">Event Details</div>

            <div className="edm-field">
              <label htmlFor="title">Title <span className="edm-required">*</span></label>
              <input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleChange}
                placeholder="e.g. Strategy Session with Jamie"
                required
              />
            </div>

            <div className="edm-field">
              <label htmlFor="description">Description</label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Add notes or agenda for this event..."
                rows="3"
              />
            </div>

            <div className="edm-field">
              <label htmlFor="location">Location</label>
              <input
                type="text"
                id="location"
                name="location"
                value={formData.location || ''}
                onChange={handleChange}
                placeholder="Office, Zoom, or leave blank"
              />
            </div>
          </div>

          {/* Section: Date & Time */}
          <div className="edm-section">
            <div className="edm-section-label">Date & Time</div>

            <div className="edm-row">
              <div className="edm-field">
                <label htmlFor="date">Date <span className="edm-required">*</span></label>
                <input
                  type="date"
                  id="date"
                  name="date"
                  value={formData.date}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="edm-field edm-color-field">
                <label htmlFor="color">Color</label>
                <div className="edm-color-picker">
                  <input
                    type="color"
                    id="color"
                    name="color"
                    value={formData.color || '#345B63'}
                    onChange={handleChange}
                  />
                  <span className="edm-color-label" style={{ color: formData.color || '#345B63' }}>
                    {formData.color || '#345B63'}
                  </span>
                </div>
              </div>
            </div>

            <div className="edm-row">
              <div className="edm-field">
                <label htmlFor="startTime">Start Time <span className="edm-required">*</span></label>
                <input
                  type="time"
                  id="startTime"
                  name="startTime"
                  value={formData.startTime}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="edm-field">
                <label htmlFor="endTime">End Time <span className="edm-required">*</span></label>
                <input
                  type="time"
                  id="endTime"
                  name="endTime"
                  value={formData.endTime}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>
          </div>

          {/* Section: Client */}
          <div className="edm-section">
            <div className="edm-section-label">Client</div>

            <div className="edm-field">
              <label htmlFor="client">Assign Client</label>
              <select
                id="client"
                name="clientId"
                value={formData.clientId}
                onChange={handleClientChange}
              >
                <option value="">— Select a Client —</option>
                {clientsList.map((client) => (
                  <option key={client._id} value={client._id}>
                    {client.firstName} {client.lastName}
                  </option>
                ))}
              </select>
            </div>

            {formData.clientEmail && (
              <div className="edm-client-info">
                <span className="edm-client-email-icon">📧</span>
                <span>{formData.clientEmail}</span>
              </div>
            )}
          </div>

          {/* Video Conference — hidden, phasing out
          <div className="edm-section">
            <div className="edm-section-label">Video Conference</div>
            <label className="edm-toggle">
              <input type="checkbox" checked={addVideoConference} onChange={handleAddVideoConferenceToggle} />
              <span className="edm-toggle-slider"></span>
              <span className="edm-toggle-text">
                {addVideoConference ? 'Video conference enabled' : 'Add video conference'}
              </span>
            </label>
            {addVideoConference && (
              <div className="edm-video-links">
                <a href={hostLink} target="_blank" rel="noopener noreferrer" className="edm-link-btn edm-link-host">
                  🎥 Open Host Room
                </a>
                <button type="button" onClick={handleCopyGuestLink} className="edm-link-btn edm-link-guest">
                  {linkCopied ? '✅ Copied!' : '📋 Copy Guest Link'}
                </button>
              </div>
            )}
          </div>
          */}

          {/* Actions */}
          <div className="edm-actions">
            <button type="submit" className="edm-btn edm-btn-save">
              {event ? 'Update Event' : 'Create Event'}
            </button>
            {event && (
              <button type="button" className="edm-btn edm-btn-delete" onClick={onDelete}>
                Delete Event
              </button>
            )}
            <button type="button" className="edm-btn edm-btn-cancel" onClick={onClose}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EventDetailsModal;